import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Check, ExternalLink, Landmark, X } from 'lucide-react'
import {
  type Dispatch,
  type SetStateAction, useEffect,
  useState
} from 'react'

import AssignBrokersToEmployer from 'components/admin/AssignBrokersToEmployer'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable, {
  getHeaderCheckboxState,
  type PaginationMetadata,
} from 'components/reusable/table/TanstackTable'
import { Checkbox } from 'components/ui/checkbox'
import { adminEmployerReviews } from 'rails_routes'
import { Badge } from 'slab'
import { getLocaleDateString } from 'utils'
import { useGetAdminEmployers, useGetAdminEmployersEmployerIdComplianceChecks } from 'utils/web_api'
import {
  AdminComplianceCheckEntityType,
  type AdminEmployer,
  type AdminEmployerBankOnboardingStatus, type AdminEmployerPlaidAccountVerificationStatus,
  type BrokersBroker,
  type GetAdminEmployersFilterBankOnboardingStatus,
  type GetAdminEmployersParams,
  type PartnersEmployerStatus,
} from 'utils/web_api.schemas'

import EmployerChecksStatus from './EmployerChecksStatus'
import EmployerComplianceEntityBadge from './EmployerComplianceEntityBadge'
import EmployerComplianceStatusBadge from './EmployerComplianceStatusBadge'
import OnboardingStatusBadge from './OnboardingStatusBadge'
import PlaidAccountVerificationStatusBadge from './PlaidAccountVerificationStatusBadge'
import EmployerReviewStatusBadge from '../brokers/EmployerReviewStatusBadge'

export const MAX_NUMBER_OF_SELECTED_ITEMS = 50

const columnHelper = createColumnHelper<AdminEmployer>()

export type EmployerFilters = {
  name?: string
  review_statuses?: string[]
  employer_ids?: string[]
  bank_partners?: string[]
  bank_onboarding_status?: string
  plaid_account_verification_status?: string[]
  onboarding_status?: string[]
  invited_employees?: string
  has_confirmed_enrollments?: string
  broker_ids?: string[]
}

type EmployerReviewsTableProps = {
  filters: EmployerFilters
  setFilters: Dispatch<SetStateAction<EmployerFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
}

const EmployerReviewsTable = ({
  filters,
  pagination,
  setPagination,
  sorting,
  setSorting,
  rowSelection,
  setRowSelection,
}: EmployerReviewsTableProps) => {
  // Convert filters and pagination to OpenAPI format
  const params: GetAdminEmployersParams & { sort?: string } = {
    'page[number]': pagination.pageIndex,
    'page[size]': pagination.pageSize,
  }

  if (filters.name) {
    params['filter[name]'] = filters.name
  }
  if (filters.review_statuses) {
    params['filter[review_statuses][]'] = filters.review_statuses as any
  }
  if (filters.employer_ids) {
    params['filter[employer_ids][]'] = filters.employer_ids
  }
  if (filters.bank_partners) {
    params['filter[bank_partners][]'] = filters.bank_partners as any
  }
  if (filters.bank_onboarding_status) {
    params['filter[bank_onboarding_status]'] = filters.bank_onboarding_status as GetAdminEmployersFilterBankOnboardingStatus
  }
  if (filters.onboarding_status) {
    params['filter[onboarding_status][]'] = filters.onboarding_status as any
  }
  if (filters.invited_employees) {
    (params as any)['filter[invited_employees]'] = filters.invited_employees === 'true'
  }
  if (filters.has_confirmed_enrollments) {
    (params as any)['filter[has_confirmed_enrollments]'] = filters.has_confirmed_enrollments === 'true'
  }
  if (filters.broker_ids) {
    (params as any)['filter[broker_ids][]'] = filters.broker_ids
  }

  // Convert sorting to OpenAPI format
  if (sorting.length > 0) {
    const { id, desc } = sorting[0]
    params.sort = `${desc ? '-' : '+'}${id}`
  }

  const {
    data: employers,
    isLoading,
    isError,
  } = useGetAdminEmployers(params)

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  const columns = [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => {
        return (
          <div onClick={(e) => e.stopPropagation()} className='flex items-center'>
            <Checkbox
              checked={getHeaderCheckboxState(table)}
              onClick={table.getToggleAllPageRowsSelectedHandler()}
            />
          </div>
        )
      },
      cell: ({ row }) => {
        return (
          <div onClick={(e) => e.stopPropagation()} className='flex items-center'>
            <Checkbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onClick={row.getToggleSelectedHandler()}
            />
          </div>
        )
      },
    }),
    columnHelper.display({
      header: 'Actions',
      cell: ({ row }: { row: Row<AdminEmployer> }) => {
        return (
          <div className='flex items-center gap-4'>
            <DisplayTooltipOnHover text='Open employer review page'>
              <div
                className='cursor-pointer h-full'
                onClick={(e) => {
                  e.preventDefault()
                  window.open(
                    adminEmployerReviews.show({ id: row.original.id }),
                    '_blank',
                    'noopener noreferrer'
                  )
                }}
              >
                <ExternalLink
                  size={20}
                  className='stroke-current text-gray-600'
                />
              </div>
            </DisplayTooltipOnHover>
          </div>
        )
      },
    }),
    columnHelper.accessor('created_at', {
      header: (info) => (
        <SortableHeader<AdminEmployer> {...info} headerText='Created' />
      ),
      cell: (info) => getLocaleDateString(new Date(info.getValue())),
      enableSorting: true,
    }),
    columnHelper.display({
      header: 'Assignees',
      cell: ({ row }) => {
        const { id, brokers } = row.original as AdminEmployer & { brokers?: BrokersBroker[] }
        return (
          <AssignBrokersToEmployer
            id={id}
            assignedBrokers={brokers || []}
            params={params}
          />
        )
      },
    }),
    columnHelper.accessor('name', {
      header: 'Employer',
      cell: (info) => {
        const employer = info.row.original
        return (
          <EmployerComplianceEntityBadge
            entityType={AdminComplianceCheckEntityType.Employer}
            entityId={employer.id}
            entityName={employer.name}
          />
        )
      },
    }),
    columnHelper.accessor('review_status', {
      header: 'Review Status',
      cell: (info) => {
        const employer = info.row.original
        return (
          <EmployerReviewStatusBadge
            employer={employer as AdminEmployer}
            shortened
          />
        )
      },
    }),
    columnHelper.accessor('onboarding_status', {
      header: 'Onboarding Status',
      cell: (info) => {
        const status = info.getValue()
        if (!status) {
          return <span className='text-gray-400 font-light'>&mdash;</span>
        }
        return <OnboardingStatusBadge status={status as PartnersEmployerStatus} />
      },
    }),
    columnHelper.accessor('compliance_audits', {
      header: 'Bank Partners',
      cell: (info) => {
        const audits = info.getValue()
        if (!audits || audits.length === 0) {
          return (
            <DisplayTooltipOnHover text='Employer will be onboarded to Unit using legacy Compliance procedures.'>
              <span className='text-gray-400 font-light'>&mdash;</span>
            </DisplayTooltipOnHover>
          )
        }

        return (
          <div className='flex flex-wrap gap-1'>
            {audits.map((audit) => (
              <Badge
                key={audit.id}
                type='info'
                leftIcon={Landmark}
              >
                {audit.partner_name || 'Unknown'}
              </Badge>
            ))}
          </div>
        )
      },
    }),
    columnHelper.accessor('bank_onboarding_status', {
      header: 'Bank Requirements',
      cell: (info) => {
        const status = info.getValue()

        return (
          <EmployerComplianceStatusBadge status={status as AdminEmployerBankOnboardingStatus} />
        )
      },
    }),
    columnHelper.display({
      header: 'Checks',
      cell: ({ row }: { row: Row<AdminEmployer> }) => {
        const employerId = row.original.id

        const { data: complianceChecks } = useGetAdminEmployersEmployerIdComplianceChecks(
          employerId,
          { query: { enabled: !!employerId } }
        )

        return (
          <EmployerChecksStatus
            checks={(complianceChecks as any[]) || []}
          />
        )
      },
    }),
    columnHelper.accessor('plaid_account_verification_status', {
      header: 'Plaid Connection',
      cell: (info) => {
        const status = info.getValue()
        return <PlaidAccountVerificationStatusBadge status={status as AdminEmployerPlaidAccountVerificationStatus} />
      },
    }),
    columnHelper.accessor('invited_employees', {
      header: 'Invites Sent?',
      cell: (info) => {
        const hasInvitedEmployees = info.getValue()
        return hasInvitedEmployees
          ? (
            <Check className='text-green-600' size={20} />
            )
          : (
            <X className='text-red-500' size={20} />
            )
      },
    }),
    columnHelper.accessor('has_confirmed_enrollments', {
      header: 'Confirmed Enrollments?',
      cell: (info) => {
        const hasConfirmedEnrollments = info.getValue()
        return hasConfirmedEnrollments
          ? (
            <Check className='text-green-600' size={20} />
            )
          : (
            <X className='text-red-500' size={20} />
            )
      },
    }),
  ]

  const table = useReactTable({
    data: employers?.data || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    state: {
      pagination,
      sorting,
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
  })

  useEffect(() => {
    if (employers) {
      setPaginationMetadata(employers.pagination)
    }
  }, [employers?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='There are no employers to review.'
      paginationLabelSingular='employer'
      paginationMetadata={paginationMetadata}
      setSorting={setSorting}
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}

export default EmployerReviewsTable
